import { injectable, decorate, inject } from 'inversify';
import 'reflect-metadata';
import { SERVICE_IDENTIFIER } from 'constants/identifiers';
import { sapFullDateFormat } from 'constants/dateFormats';
import { convertDateFormat } from 'utils/date';
import type { PromiseTaskQuery } from 'screens/promises/model/consts';
import type {
  PromiseFormValue,
  PromiseTask,
} from 'screens/promises/model/types';

interface ApiService {
  get: (
    route: string,
    params: { MODE: PromiseTaskQuery },
    config: { signal?: AbortSignal },
  ) => Promise<{ data: { data: { items: PromiseTask[] } } }>;
  post: (route: string, data: PromiseActionPayload) => Promise<unknown>;
}

interface PromisePayloadItem {
  contract: string;
  date: string;
  amount: string;
}

interface PromiseActionPayload {
  command: 'PROMISE' | 'NOT_AGREED';
  todo: string;
  promises: PromisePayloadItem[];
}

class PromiseService {
  private apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }

  async getPromiseTasks(
    params: { MODE: PromiseTaskQuery },
    abortSignal?: AbortSignal,
  ) {
    const result = await this.apiService.get('/promises/list', params, {
      signal: abortSignal,
    });

    return result.data.data.items;
  }

  static mapPromisesToPayload(
    promises: PromiseFormValue[],
  ): PromisePayloadItem[] {
    return promises
      .filter(
        (promise): promise is PromiseFormValue & {
          contract: NonNullable<PromiseFormValue['contract']>;
          paymentDate: Date;
        } =>
          Boolean(
            promise?.contract?.id && promise.amount && promise.paymentDate,
          ),
      )
      .map((promise) => ({
        contract: promise.contract.id,
        amount: String(promise.amount),
        date: convertDateFormat(promise.paymentDate, sapFullDateFormat),
      }));
  }

  async createPromises(taskId: string, promises: PromiseFormValue[]) {
    return this.apiService.post('/promises/action', {
      command: 'PROMISE',
      todo: taskId,
      promises: PromiseService.mapPromisesToPayload(promises),
    });
  }

  async cantAgree(taskId: string) {
    return this.apiService.post('/promises/action', {
      command: 'NOT_AGREED',
      todo: taskId,
      promises: [],
    });
  }
}

decorate(injectable() as ClassDecorator, PromiseService);
decorate(
  inject(SERVICE_IDENTIFIER.ApiService) as unknown as ParameterDecorator,
  PromiseService,
  0,
);

export default PromiseService;
